import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Compass, Sparkles, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { CaraLoomLogo } from '../components/brand/CaraLoomLogo';

/**
 * ParkedWorkspace — graceful single screen for roles whose workspace is
 * parked (currently: payer). Same pattern as ArchivedFeaturePage: the
 * role, the users, and the underlying dashboard code are all preserved —
 * only the surface is hidden while the platform focuses on the
 * discharge loop.
 */
export default function ParkedWorkspace() {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen bg-cn-bg text-cn-text flex flex-col">
      <header className="px-6 md:px-10 py-5 flex items-center justify-between border-b border-cn-border">
        <CaraLoomLogo />
        {user && (
          <button
            onClick={logout}
            className="inline-flex items-center gap-1.5 text-sm text-cn-text-muted hover:text-cn-primary"
            data-testid="parked-signout"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        )}
      </header>
      <section className="flex-1 flex items-center justify-center px-6 py-16" data-testid="parked-workspace">
        <div className="max-w-2xl text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cn-primary/10 border border-cn-primary/20 mb-6">
            <Sparkles className="w-3.5 h-3.5 text-cn-primary" aria-hidden="true" />
            <span className="text-xs font-semibold tracking-wide uppercase text-cn-primary">
              On the roadmap
            </span>
          </div>
          <h1 className="cn-display text-4xl sm:text-5xl mb-5">
            This workspace isn&apos;t available yet.
          </h1>
          <p className="text-lg text-cn-text-muted leading-relaxed mb-8">
            CaraLoom is focused on one thing today: moving every discharge from
            referral to a committed first visit — automatically. The payer
            analytics workspace returns once that loop is running at scale.
            Your account and data are preserved; nothing was deleted.
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Link
              to="/how-it-works"
              className="cn-btn cn-btn-primary inline-flex items-center gap-2"
              data-testid="parked-how-it-works"
            >
              <Compass className="w-4 h-4" /> See what&apos;s live today
            </Link>
            <Link
              to="/contact"
              className="cn-btn cn-btn-ghost inline-flex items-center gap-2"
              data-testid="parked-contact"
            >
              Talk to us <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
